import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { batch } from 'react-redux';
import { isEmpty, prop } from 'ramda';
import { useForm } from 'react-final-form';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { useGetErrorMessage } from 'js/hooks/server';
import { Hidden } from 'js/components/common/Fields/Hidden';
import { useGetBirthCountries, useGetRegionsByCountryCode } from 'server';
import { IBirthCountry } from 'js/hooks/socket/hooks/useGetBirthCountries/types';
import { ICountryRegion } from 'js/hooks/socket/hooks/useGetRegionsByCountryCode/types';

import * as helpers from './helpers';
import * as actions from '../../store/actions';
import { cmsDateStringToDate } from '../../helpers';
import { useDynamicReducerDispatch, useDynamicReducerSelector } from '../../store';

import * as Types from './types';

export const PlaceOfResidenceFieldBare: React.FC<Types.IPlaceOfResidenceFieldConfig> = ({
    field,
    disabled,
    formID,
    className,
    isFieldHidden,
    ChangeFormField
}) => {
    const formAPI = useForm();
    const isMounted = useIsMounted();
    const getErrorMessage = useGetErrorMessage();
    const [storedCountries, setCountries] = useState<IBirthCountry[]>([]);
    const [storedRegions, setRegions] = useState<ICountryRegion[]>([]);
    const [areCountriesLoading, setCountriesLoading] = useState(false);
    const [areRegionsLoading, setRegionsLoading] = useState(false);
    const [validityDate, setValidityDate] = useState<string | null>(null);
    const [selectedProvince, setSelectedProvince] = useState('');

    const dispatch = useDynamicReducerDispatch();
    const getBirthCountries = useGetBirthCountries();
    const getRegionsByCountryCode = useGetRegionsByCountryCode();
    const hiddenFields = useDynamicReducerSelector(prop('hiddenFields'));
    const { dateCondition, countryCondition, name, innerFields: innerFieldsRaw } = field;

    const innerFields = useMemo(() => {
        return {
            ...innerFieldsRaw.countryOfResidence && { countryOfResidence: innerFieldsRaw.countryOfResidence },
            ...innerFieldsRaw.countryOfResidence && innerFieldsRaw.provinceOfResidence
            && { provinceOfResidence: innerFieldsRaw.provinceOfResidence },
            ...innerFieldsRaw.countryOfResidence && innerFieldsRaw.provinceOfResidence && innerFieldsRaw.cityOfResidence
            && { cityOfResidence: innerFieldsRaw.cityOfResidence }
        };
    }, [innerFieldsRaw]);
    const countryOfResidenceField = useMemo(() => ({
        ...innerFields.countryOfResidence && {
            ...innerFields.countryOfResidence,
            customList: helpers.birthCountriesToOptions(storedCountries || [])
        }
    }), [innerFields, storedCountries]);

    const provinceOfResidenceField = useMemo(() => ({
        ...innerFields.provinceOfResidence && {
            ...innerFields.provinceOfResidence,
            customList: helpers.countryRegionsToOptions(storedRegions || [])
        }
    }), [innerFields, storedRegions]);

    const cityOfResidenceField = useMemo(() => ({
        ...innerFields.cityOfResidence && {
            ...innerFields.cityOfResidence,
            customList: helpers.countryCitiesToOptions(storedRegions, selectedProvince)
        }
    }), [innerFields, selectedProvince, storedRegions]);

    const addHiddenField = useCallback((fieldName: string | undefined) => {
        fieldName && dispatch(actions.addHiddenField(fieldName));
    }, [dispatch]);

    const removeHiddenField = useCallback((fieldName: string | undefined) => {
        fieldName && dispatch(actions.removeHiddenField(fieldName));
    }, [dispatch]);

    const formAPIChange = useCallback((fieldName: string | undefined, value: string) => {
        fieldName && formAPI.change(fieldName, value);
    }, [formAPI]);

    useEffect(() => {
        if (validityDate) {
            return;
        }

        const date = cmsDateStringToDate(dateCondition) || new Date();
        const isoDate = date.toJSON().slice(0, 10);

        setValidityDate(isoDate);
        setCountries([]);
        setCountriesLoading(true);

        batch(() => {
            addHiddenField(countryOfResidenceField.name);
            addHiddenField(provinceOfResidenceField.name);
            addHiddenField(cityOfResidenceField.name);
        });

        (async () => {
            try {
                const { birthCountries } = await getBirthCountries(isoDate);

                if (!isMounted()) {
                    return;
                }

                setCountries(birthCountries);

                removeHiddenField(countryOfResidenceField.name);
            } catch (error: any) {
                isMounted() && dispatch(actions.setFormInternalError(getErrorMessage(error)));
            } finally {
                isMounted() && setCountriesLoading(false);
            }
        })();
    }, [
        countryOfResidenceField,
        cityOfResidenceField,
        dispatch,
        addHiddenField,
        removeHiddenField,
        dateCondition,
        formAPI,
        getBirthCountries,
        getErrorMessage,
        isMounted,
        provinceOfResidenceField,
        validityDate
    ]);

    const onCountryChanged = useCallback((event: { target: { value: string } }) => {
        if (isEmpty(provinceOfResidenceField)) {
            return;
        }

        const countryCode = event.target.value;

        formAPI.batch(() => {
            formAPIChange(name, '');
            formAPIChange(provinceOfResidenceField.name, '');
            formAPIChange(cityOfResidenceField.name, '');
        });

        batch(() => {
            dispatch(actions.setFormInternalError(''));
            addHiddenField(provinceOfResidenceField.name);
            addHiddenField(cityOfResidenceField.name);
        });

        if (!validityDate || countryCode !== countryCondition) {
            return;
        }

        setRegions([]);
        setRegionsLoading(true);

        (async () => {
            try {
                const { regionsByCountryCode } = await getRegionsByCountryCode({
                    validityDate,
                    countryCode
                });

                if (!isMounted()) {
                    return;
                }

                setRegions(regionsByCountryCode);

                removeHiddenField(provinceOfResidenceField.name);
            } catch (error: any) {
                isMounted() && dispatch(actions.setFormInternalError(getErrorMessage(error)));
            } finally {
                isMounted() && setRegionsLoading(false);
            }
        })();
    }, [
        cityOfResidenceField,
        dispatch,
        formAPI,
        formAPIChange,
        addHiddenField,
        removeHiddenField,
        name,
        countryCondition,
        getErrorMessage,
        getRegionsByCountryCode,
        isMounted,
        provinceOfResidenceField,
        validityDate
    ]);

    const onProvinceChange = useCallback((event: { target: { value: string } }) => {
        if (isEmpty(cityOfResidenceField)) {
            return;
        }

        const provinceCode = event.target.value;
        const sogeiRegionCode = helpers.getSogeiRegionCode({ provinceCode, countryRegions: storedRegions });

        formAPI.batch(() => {
            formAPI.change(name, sogeiRegionCode);
            formAPIChange(cityOfResidenceField.name, '');
        });

        setSelectedProvince(provinceCode);
        removeHiddenField(cityOfResidenceField.name);
    }, [cityOfResidenceField, name, formAPI, formAPIChange, storedRegions, removeHiddenField]);

    const showCountryOfResidenceField = !areCountriesLoading
        && Boolean(countryOfResidenceField.customList?.length)
        && countryOfResidenceField.name && !hiddenFields.includes(countryOfResidenceField.name);

    const showResidenceProvinceField = showCountryOfResidenceField
        && Boolean(provinceOfResidenceField.customList?.length)
        && provinceOfResidenceField.name && !hiddenFields.includes(provinceOfResidenceField.name);

    const showCityOfResidenceField = showResidenceProvinceField
        && Boolean(cityOfResidenceField.customList?.length)
        && cityOfResidenceField.name && !hiddenFields.includes(cityOfResidenceField.name);

    return (
        <>
            {!isEmpty(countryOfResidenceField) && <Hidden name={field.name} />}
            {showCountryOfResidenceField && (
                <ChangeFormField
                    key={countryOfResidenceField.name}
                    formID={formID}
                    className={className}
                    onChange={onCountryChanged}
                    isFieldHidden={isFieldHidden}
                    field={countryOfResidenceField}
                    disabled={disabled || areRegionsLoading}
                />
            )}
            {showResidenceProvinceField && (
                <ChangeFormField
                    key={provinceOfResidenceField.name}
                    formID={formID}
                    disabled={disabled}
                    className={className}
                    onChange={onProvinceChange}
                    isFieldHidden={isFieldHidden}
                    field={provinceOfResidenceField}
                />
            )}
            {showCityOfResidenceField && (
                <ChangeFormField
                    key={cityOfResidenceField.name}
                    formID={formID}
                    disabled={disabled}
                    className={className}
                    field={cityOfResidenceField}
                    isFieldHidden={isFieldHidden}
                />
            )}
        </>
    );
};

export const PlaceOfResidenceField = memo(PlaceOfResidenceFieldBare);
